import { apiFetch } from "../../lib/api";
import { useState, useEffect, useCallback } from "react";
import { FileTree, getExtColor } from "./FileTree";
import type { FileNode } from "./FileTree";

/* ── Mock tree (same as OpentoolCode fallback) ── */
function buildMockTree(): FileNode[] {
  return [
    {
      name: "src", path: "src", type: "dir", children: [
        {
          name: "bin", path: "src/bin", type: "dir", children: [
            { name: "opentool-web.rs", path: "src/bin/opentool-web.rs", type: "file" },
            { name: "opentool-admin.rs", path: "src/bin/opentool-admin.rs", type: "file" },
          ],
        },
        {
          name: "engine", path: "src/engine", type: "dir", children: [
            { name: "mod.rs", path: "src/engine/mod.rs", type: "file" },
            { name: "router.rs", path: "src/engine/router.rs", type: "file" },
            { name: "routing_policy.rs", path: "src/engine/routing_policy.rs", type: "file" },
          ],
        },
        { name: "lib.rs", path: "src/lib.rs", type: "file" },
        { name: "org.rs", path: "src/org.rs", type: "file" },
      ],
    },
    {
      name: "ui", path: "ui", type: "dir", children: [
        {
          name: "src", path: "ui/src", type: "dir", children: [
            { name: "App.tsx", path: "ui/src/App.tsx", type: "file" },
            {
              name: "components", path: "ui/src/components", type: "dir", children: [
                { name: "desktop", path: "ui/src/components/desktop", type: "dir", children: [] },
                { name: "chat", path: "ui/src/components/chat", type: "dir", children: [] },
                { name: "notebook", path: "ui/src/components/notebook", type: "dir", children: [] },
              ],
            },
          ],
        },
      ],
    },
    { name: "Cargo.toml", path: "Cargo.toml", type: "file" },
    { name: "package.json", path: "package.json", type: "file" },
    { name: "README.md", path: "README.md", type: "file" },
  ];
}

export function FileExplorer() {
  const [tree, setTree] = useState<FileNode[]>([]);
  const [selectedPath, setSelectedPath] = useState<string | undefined>();
  const [breadcrumb, setBreadcrumb] = useState<string[]>([]);

  useEffect(() => {
    apiFetch("/api/files")
      .then((r) => {
        if (!r.ok) throw new Error("no api");
        return r.json();
      })
      .then((d) => {
        if (d.tree && d.tree.length > 0) setTree(d.tree);
        else setTree(buildMockTree());
      })
      .catch(() => setTree(buildMockTree()));
  }, []);

  const handleFileSelect = useCallback((path: string) => {
    setSelectedPath(path);
    setBreadcrumb(path.split("/"));
    // Open the file in opentool_code (files app)
    window.dispatchEvent(
      new CustomEvent("opentool-open-app", { detail: { id: "files", payload: { openFile: path } } })
    );
  }, []);

  // Count files recursively
  const countFiles = useCallback((nodes: FileNode[]): number => {
    let count = 0;
    for (const n of nodes) {
      if (n.type === "file") count++;
      if (n.children) count += countFiles(n.children);
    }
    return count;
  }, []);

  const fileCount = countFiles(tree);

  return (
    <div className="fex-layout">
      {/* Header */}
      <div className="fex-header">
        <div className="fex-header__title">
          <svg viewBox="0 0 16 16" fill="none" className="fex-header__icon">
            <path
              d="M1.5 3v10h13V5H7.5l-1-2H1.5z"
              fill="rgba(79,143,255,0.25)"
              stroke="rgba(79,143,255,0.6)"
              strokeWidth="0.8"
            />
          </svg>
          <span>File Explorer</span>
        </div>
        {breadcrumb.length > 0 && (
          <div className="fex-header__breadcrumb">
            {breadcrumb.map((seg, i) => (
              <span key={i} className="fex-header__breadcrumb-seg">
                {i > 0 && <span className="fex-header__breadcrumb-sep">{"\u203A"}</span>}
                <span
                  className={i === breadcrumb.length - 1 ? "fex-header__breadcrumb-current" : ""}
                >
                  {seg}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Tree area */}
      <div className="fex-tree">
        {tree.length === 0 ? (
          <div className="fex-loading">Loading file tree...</div>
        ) : (
          <FileTree
            files={tree}
            onFileSelect={handleFileSelect}
            selectedPath={selectedPath}
            defaultExpanded={new Set(["src", "ui"])}
          />
        )}
      </div>

      {/* Footer status */}
      <div className="fex-footer">
        <span className="fex-footer__count">{fileCount} items</span>
        {selectedPath && (
          <span className="fex-footer__selected">
            <span className="fex-footer__dot" style={{ background: getExtColor(selectedPath.split("/").pop() || "") }} />
            {selectedPath.split("/").pop()}
          </span>
        )}
        <span className="fex-footer__hint">
          <kbd>Enter</kbd> Open in Editor
        </span>
      </div>
    </div>
  );
}
